import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";
import { api } from "@/api";
import { formatError } from "@/components/shared/helpers";
import { useAuth } from "@/components/shared/AuthContext";
import { useTranslation } from "@/components/shared/i18n";
import { PasswordStrengthChecklist, isPasswordStrong } from "@/components/shared/PasswordRules";

/**
 * Écran obligatoire à la première connexion (must_change_password=true).
 * Impossible de bypasser : la route est appelée depuis App.js dès que
 * `me.must_change_password` est vrai et bloque tout accès aux autres pages.
 */
export default function FirstPasswordChangePage() {
  const navigate = useNavigate();
  const { refresh, logout } = useAuth();
  const { t } = useTranslation();
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const passLongEnough = isPasswordStrong(newPassword);
  const passesMatch = newPassword && newPassword === confirm;
  const canSubmit = passLongEnough && passesMatch && !submitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      // current_password ignoré côté backend quand must_change_password=true
      await api.put("/owner/password", {
        current_password: "",
        new_password: newPassword,
      });
      toast({ title: t("first_pwd.success") || "Mot de passe défini" });
      await refresh();
      navigate("/owner", { replace: true });
    } catch (err) {
      toast({
        title: t("first_pwd.error") || "Erreur",
        description: formatError(err),
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="first-pwd-page"
      className="min-h-screen bg-[#060A14] text-slate-50 flex items-center justify-center px-4 py-12"
    >
      <Card
        data-testid="first-pwd-card"
        className="w-full max-w-md border-white/10 bg-[#0b0d12]/80 backdrop-blur"
      >
        <CardHeader>
          <CardTitle
            data-testid="first-pwd-title"
            className="text-center text-slate-50 text-xl"
          >
            Choisissez votre mot de passe
          </CardTitle>
          <p
            data-testid="first-pwd-subtitle"
            className="text-center text-sm text-slate-300 mt-2"
          >
            Première connexion — définissez votre mot de passe personnel avant
            d&apos;accéder au portail.
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1">
              <Label
                data-testid="first-pwd-new-label"
                htmlFor="first-pwd-new"
                className="text-slate-200"
              >
                Nouveau mot de passe
              </Label>
              <Input
                data-testid="first-pwd-new-input"
                id="first-pwd-new"
                type="password"
                autoComplete="new-password"
                className="border-white/10 bg-white/[0.08]"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
              />
              <PasswordStrengthChecklist password={newPassword} testIdPrefix="first-pwd" />
            </div>

            <div className="space-y-1">
              <Label
                data-testid="first-pwd-confirm-label"
                htmlFor="first-pwd-confirm"
                className="text-slate-200"
              >
                Confirmer le mot de passe
              </Label>
              <Input
                data-testid="first-pwd-confirm-input"
                id="first-pwd-confirm"
                type="password"
                autoComplete="new-password"
                className="border-white/10 bg-white/[0.08]"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
              />
              {confirm && !passesMatch && (
                <div
                  data-testid="first-pwd-hint-match"
                  className="text-xs text-rose-300"
                >
                  Les deux mots de passe ne correspondent pas.
                </div>
              )}
            </div>

            <Button
              data-testid="first-pwd-submit"
              type="submit"
              className="w-full rounded-full bg-violet-500 text-white hover:bg-violet-400 disabled:opacity-50"
              disabled={!canSubmit}
            >
              {submitting ? "Enregistrement…" : "Définir mon mot de passe"}
            </Button>

            <Button
              data-testid="first-pwd-logout"
              type="button"
              variant="outline"
              className="w-full rounded-full border-white/10 bg-white/[0.04] text-slate-200 hover:bg-white/[0.08]"
              onClick={logout}
            >
              Me déconnecter
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
